import { isFiveProductLaunchLive } from "./five-product-launch";

/**
 * Central mapping: peptide slug → available deep-links.
 * Used by Stack Builder (PeptideCard) and Quiz (RecommendationCard).
 *
 * - productSlug: slug of the product in Saleor (used for PDP links).
 * - guideSlug: slug of the guide Saleor Page (/guides/<slug>), if one exists.
 * - relatedGuideSlugs: extra pillar/how-to guide slugs to surface alongside the monograph (live slugs only).
 * - calculatorSlug: path segment under /peptide-calculator/. Must match a slug in PEPTIDE_DATA.
 * - halfLifeSlug: slug for /half-life-calculator/<slug>, populated when
 *   the peptide has halfLifeHours > 0 in PEPTIDE_DATA.
 */
export interface PeptideLinks {
	/** Omit when the peptide is not sold in the shop (calculator/guide links may still apply). */
	productSlug?: string;
	guideSlug?: string;
	/** Extra pillar/how-to guides to surface on the PDP beyond the canonical monograph guideSlug. Live slugs only. */
	relatedGuideSlugs?: string[];
	calculatorSlug: string;
	halfLifeSlug?: string;
}

function launchProductLink(slug: string): Pick<PeptideLinks, "productSlug"> {
	return isFiveProductLaunchLive(slug) ? { productSlug: slug } : {};
}

export const PEPTIDE_LINKS: Record<string, PeptideLinks> = {
	pinealon: {
		productSlug: "pinealon",
		calculatorSlug: "pinealon",
	},
	survodutide: {
		productSlug: "survodutide",
		calculatorSlug: "survodutide",
	},
	// Verified 2026-09-23: 10 mg vial, 1/2/3-pack variants, live and purchasable in EU and US.
	"kisspeptin-10": {
		productSlug: "kisspeptin-10",
		calculatorSlug: "kisspeptin-10",
	},
	dsip: {
		productSlug: "dsip",
		guideSlug: "dsip-leitfaden",
		calculatorSlug: "dsip",
	},
	"5-amino-1mq": {
		productSlug: "5-amino-1mq",
		guideSlug: "5-amino-1mq-leitfaden",
		calculatorSlug: "5-amino-1mq",
	},
	sermorelin: {
		productSlug: "sermorelin",
		calculatorSlug: "sermorelin",
	},
	// VIP and PT-141 guides are release-policy pages (6 locales); getGuideCards drops them elsewhere.
	vip: {
		...launchProductLink("vip"),
		guideSlug: "vip-peptid-leitfaden",
		calculatorSlug: "vip",
	},
	cagrilintide: {
		...launchProductLink("cagrilintide"),
		guideSlug: "cagrilintide-leitfaden",
		relatedGuideSlugs: ["peptide-zum-abnehmen-leitfaden"],
		calculatorSlug: "cagrilintide",
	},
	"ahk-cu": {
		...launchProductLink("ahk-cu"),
		relatedGuideSlugs: ["kupferpeptide-haut-haar-leitfaden"],
		calculatorSlug: "ahk-cu",
	},
	"pt-141": {
		...launchProductLink("pt-141"),
		guideSlug: "pt-141-leitfaden",
		calculatorSlug: "pt-141",
	},
	"melanotan-1": {
		...launchProductLink("melanotan-1"),
		calculatorSlug: "melanotan-1",
	},
	"bpc-157": {
		productSlug: "bpc-157",
		guideSlug: "bpc-157-leitfaden",
		relatedGuideSlugs: ["peptide-regeneration-leitfaden"],
		calculatorSlug: "bpc-157",
	},
	tb500: {
		productSlug: "tb-500",
		guideSlug: "tb-500-leitfaden",
		relatedGuideSlugs: ["peptide-regeneration-leitfaden"],
		calculatorSlug: "tb-500",
	},
	retatrutide: {
		productSlug: "retatrutide",
		guideSlug: "retatrutide-leitfaden",
		relatedGuideSlugs: ["retatrutide-kaufen", "peptide-zum-abnehmen-leitfaden"],
		calculatorSlug: "retatrutide",
	},
	tirzepatid: {
		productSlug: "tirzepatid",
		guideSlug: "tirzepatid-leitfaden",
		relatedGuideSlugs: ["tirzepatid-dosierung-einnahme-leitfaden", "peptide-zum-abnehmen-leitfaden"],
		calculatorSlug: "tirzepatid",
	},
	"mt-2": {
		productSlug: "mt-2",
		guideSlug: "melanotan-ii-leitfaden",
		calculatorSlug: "melanotan-2",
	},
	semax: {
		productSlug: "semax",
		guideSlug: "semax-leitfaden",
		calculatorSlug: "semax",
	},
	// Selank — live + purchasable in BOTH channels (10 mg vials, 1/2/3 pack; verified
	// against the prod API 2026-08-21). It had no productSlug here while the product was
	// on sale, which was the only reason its spoke never got a buy card. No guide page
	// exists yet, so guideSlug stays unset.
	selank: {
		productSlug: "selank",
		calculatorSlug: "selank",
	},
	tesamorelin: {
		productSlug: "tesamorelin",
		guideSlug: "tesamorelin-leitfaden",
		calculatorSlug: "tesamorelin",
	},
	"ghk-cu": {
		productSlug: "ghk-cu",
		guideSlug: "ghk-cu-leitfaden",
		relatedGuideSlugs: ["kupferpeptide-haut-haar-leitfaden", "peptide-anti-aging-leitfaden"],
		calculatorSlug: "ghk-cu",
	},
	"hgh-fragment-176-191": {
		calculatorSlug: "hgh-fragment-176-191",
	},
	// hGH 191aa — delisted: the product resolves to null in BOTH channels (verified
	// 2026-08-21), so /products/hgh-191aa answers 200 + noindex, a dead end. Calculator
	// links stay; the productSlug is withdrawn so no buy CTA can be built from it.
	"hgh-191aa": {
		calculatorSlug: "hgh-191aa",
	},
	"nad-plus": {
		productSlug: "nad-plus",
		guideSlug: "nad-plus-leitfaden",
		relatedGuideSlugs: ["peptide-anti-aging-leitfaden"],
		calculatorSlug: "nad-plus",
	},
	"mots-c": {
		productSlug: "mots-c",
		guideSlug: "mots-c-leitfaden",
		relatedGuideSlugs: ["peptide-anti-aging-leitfaden"],
		calculatorSlug: "mots-c",
	},
	"cjc-1295": {
		productSlug: "cjc-1295",
		guideSlug: "cjc-1295-leitfaden",
		calculatorSlug: "cjc-1295-no-dac",
	},
	kpv: {
		productSlug: "kpv",
		guideSlug: "kpv-leitfaden",
		relatedGuideSlugs: ["peptide-regeneration-leitfaden"],
		calculatorSlug: "kpv",
	},
	// Ipamorelin — live + purchasable in eu (verified 2026-08-11); guide published in Saleor.
	ipamorelin: {
		productSlug: "ipamorelin",
		guideSlug: "ipamorelin-leitfaden",
		calculatorSlug: "ipamorelin",
	},
	// Epithalon — live + purchasable in eu (verified 2026-08-11); guide published in Saleor.
	epithalon: {
		productSlug: "epithalon",
		guideSlug: "epithalon-leitfaden",
		calculatorSlug: "epithalon",
	},
	// SS-31 (Elamipretide) — live + in stock in both channels (10 mg vials, 1/2/3 pack).
	// Both guide slugs verified published in Saleor before registering them here.
	"ss-31": {
		productSlug: "ss-31",
		guideSlug: "ss-31-leitfaden",
		relatedGuideSlugs: ["peptide-anti-aging-leitfaden"],
		calculatorSlug: "ss-31",
	},
	"thymosin-alpha-1": {
		productSlug: "thymosin-alpha-1",
		guideSlug: "thymosin-alpha-1-leitfaden",
		relatedGuideSlugs: ["peptide-regeneration-leitfaden"],
		calculatorSlug: "thymosin-alpha-1",
	},
	// Klow Stack bundle — verified live in Saleor (both eu + us channels). Registered so
	// Stack-Builder / Quiz result cards can deep-link it. (The CJC+Ipamorelin blend that
	// was also requested does NOT exist as a Saleor product, so it is intentionally omitted.)
	"klow-stack": {
		productSlug: "klow-stack",
		guideSlug: "klow-stack-leitfaden",
		relatedGuideSlugs: ["kupferpeptide-haut-haar-leitfaden", "peptide-regeneration-leitfaden"],
		calculatorSlug: "klow-stack",
	},
	// Glow Stack bundle — verified live in Saleor (both eu + us channels), same as Klow Stack above.
	"glow-stack": {
		productSlug: "glow-stack",
		guideSlug: "glow-stack-peptide-dosierung-leitfaden",
		relatedGuideSlugs: ["kupferpeptide-haut-haar-leitfaden"],
		calculatorSlug: "glow-stack",
	},
};

import { PEPTIDE_DATA } from "@/lib/calculator/peptide-data";

function resolvePeptideLinkEntry(slug: string): PeptideLinks | undefined {
	if (Object.hasOwn(PEPTIDE_LINKS, slug)) return PEPTIDE_LINKS[slug];
	return Object.values(PEPTIDE_LINKS).find(
		(entry) => entry.productSlug === slug || entry.calculatorSlug === slug,
	);
}

/**
 * Resolve the live, purchasable product slug for a *calculator* slug (a PEPTIDE_DATA slug
 * such as "tb-500" / "melanotan-2" / "cjc-1295-no-dac").
 *
 * PEPTIDE_LINKS is keyed by an arbitrary key (NOT the calculator slug), so the shared
 * resolver also matches product/calculator slugs (tb-500→key tb500,
 * melanotan-2→key mt-2, cjc-1295-no-dac→key cjc-1295). Returns null when the peptide has no
 * live single-product entry, so callers degrade to the learn-more link instead of a 404.
 *
 * This is the verified live-gate (PEPTIDE_LINKS is the curated allow-list); do NOT use the
 * unreliable `hasBergdorfProduct` flag in PEPTIDE_DATA for this.
 */
export function resolveBuyBridgeProductSlug(calcSlug: string): string | null {
	return resolvePeptideLinkEntry(calcSlug)?.productSlug ?? null;
}

/** Resolve registry keys, Saleor product slugs and calculator slugs to the same canonical links. */
export function getPeptideLinks(slug: string): PeptideLinks | null {
	const base = resolvePeptideLinkEntry(slug);
	if (!base) return null;

	// Stack-builder / quiz slugs (e.g. "tb500", "mt-2") don't always match PEPTIDE_DATA slugs
	// (e.g. "tb-500", "melanotan-2"). Resolve via the shared productSlug, which is the Saleor /
	// PEPTIDE_DATA-aligned identifier.
	const peptide =
		PEPTIDE_DATA.find((p) => p.slug === slug) ??
		PEPTIDE_DATA.find((p) => p.slug === base.productSlug) ??
		PEPTIDE_DATA.find((p) => p.slug === base.calculatorSlug);
	const halfLifeSlug =
		peptide && typeof peptide.halfLifeHours === "number" && peptide.halfLifeHours > 0
			? peptide.slug
			: undefined;

	return { ...base, halfLifeSlug };
}

/**
 * Live guides for PDPs without a PEPTIDE_LINKS entry. Blends have no calculator
 * profile, and a PEPTIDE_LINKS entry would invent calculator and category links.
 */
const PRODUCT_ONLY_GUIDE_SLUGS: Record<string, string[]> = {
	"cjc-ipa-blend": ["cjc-1295-ipamorelin-blend-leitfaden"],
};

/**
 * All guide slugs to surface on a product's PDP: canonical monograph first,
 * then relatedGuideSlugs. Reverse-looks-up by Saleor product slug. Returns []
 * when the product has no mapped guides. getGuideCards() filters unpublished
 * slugs downstream, so draft/missing guides never render.
 */
export function getProductGuideSlugs(productSlug: string): string[] {
	const entry = resolvePeptideLinkEntry(productSlug);
	if (entry?.productSlug !== productSlug) {
		return Object.hasOwn(PRODUCT_ONLY_GUIDE_SLUGS, productSlug)
			? [...PRODUCT_ONLY_GUIDE_SLUGS[productSlug]]
			: [];
	}
	return [
		...new Set([entry.guideSlug, ...(entry.relatedGuideSlugs ?? [])].filter((s): s is string => Boolean(s))),
	];
}
